import { useState, useCallback } from 'react';
import { useCreateScheduleEntry } from './useCreateScheduleEntry';
import { lookupErrorCopy } from '../lib/errorMap';
import { useAuthStore } from '../stores/authStore';
import { localWallClockToInstant } from '../components/schedule/scheduleHelpers';

interface CreateEventData {
  business_id: string;
  title: string;
  date: string;
  all_day: boolean;
  start_time?: string;
  end_time?: string;
  description?: string;
  visibility: string;
  recurrence: string;
  created_by: string;
}

export function useCreateEvent() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const createScheduleEntry = useCreateScheduleEntry();

  const createEvent = useCallback(async (data: CreateEventData): Promise<boolean> => {
    setLoading(true); setError(null);
    try {
      if (!data.title.trim()) { setError('Title is required'); setLoading(false); return false; }
      if (!data.date) { setError('Date is required'); setLoading(false); return false; }

      // start_at/end_at are timestamptz — build true instants at the business tz
      // (A2 P2), not naive wall-clock-as-UTC strings.
      const tz = useAuthStore.getState().business?.timezone ?? null;
      const startAt = data.all_day
        ? localWallClockToInstant(data.date, '00:00:00', tz)
        : localWallClockToInstant(data.date, `${data.start_time || '09:00'}:00`, tz);
      const endAt = data.all_day
        ? localWallClockToInstant(data.date, '23:59:59', tz)
        : localWallClockToInstant(data.date, `${data.end_time || '10:00'}:00`, tz);
      const rrule = data.recurrence !== 'none' ? `FREQ=${data.recurrence.toUpperCase()}` : undefined;

      const res = await createScheduleEntry({
        type: 'event',
        title: data.title.trim(),
        startAt,
        endAt,
        notes: data.description || undefined,
        recurrenceRule: rrule,
        idempotencyKey: crypto.randomUUID(),
      });
      if (!res.success) {
        setError(lookupErrorCopy('create_schedule_entry', res.error.code));
        setLoading(false);
        return false;
      }
      setLoading(false);
      return true;
    } catch (err) { setError(err instanceof Error ? err.message : 'Failed to create event'); setLoading(false); return false; }
  }, [createScheduleEntry]);

  return { createEvent, loading, error };
}
